<template>
  <div class="listApplyRoomDiscountRecordDetails-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listApplyRoomDiscountRecordDetails.page.title')"
      :subtitle="applyRoomDiscountRecordDetailsInfo.conditions.roomName">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('listApplyRoomDiscountRecordDetails.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listApplyRoomDiscountRecordDetails">
        <template #left>
          <span class="ds-panel__title">{{ $t('listApplyRoomDiscountRecordDetails.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="applyRoomDiscountRecordDetailsInfo.applyRoomDiscountRecordDetails" :loading="loading"
        :error="hasError" :error-message="errorMessage" :density="density" border
        :empty-description="$t('listApplyRoomDiscountRecordDetails.page.empty')"
        @retry="_listApplyRoomDiscountRecordDetails">
        <el-table-column prop="roomName" :label="$t('listApplyRoomDiscountRecordDetails.room')" align="left"
          min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.roomName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('listApplyRoomDiscountRecordDetails.image')" align="center" width="180">
          <template slot-scope="scope">
            <!-- 图片 -->
            <div v-if="scope.row.relTypeCd === '19000'" class="ds-media" @click="showImg(scope.row.url)">
              <img class="ds-media__thumb" :src="scope.row.url" @error="handleImageError" alt="" />
              <img class="ds-media__mask" src="/img/icon-bigimg.png" alt="" />
            </div>
            <!-- 视频 -->
            <video v-else-if="scope.row.relTypeCd === '21000'" class="ds-media__video" controls autoplay>
              <source :src="scope.row.url" type="video/mp4">
            </video>
            <span v-else class="ds-text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('listApplyRoomDiscountRecordDetails.createTime')" align="center"
          width="180">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('listApplyRoomDiscountRecordDetails.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <view-image ref="viewImage" />
  </div>
</template>

<script>
import { queryApplyRoomDiscountRecordDetail } from '@/api/fee/listApplyRoomDiscountRecordDetailsApi'
import { getCommunityId } from '@/api/community/communityApi'
import ViewImage from '@/components/system/viewImage'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListApplyRoomDiscountRecordDetails',
  components: {
    ViewImage,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      applyRoomDiscountRecordDetailsInfo: {
        applyRoomDiscountRecordDetails: [],
        conditions: {
          ardrId: '',
          roomName: '',
          state: '',
          roomId: '',
          communityId: ''
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.applyRoomDiscountRecordDetailsInfo.conditions.ardrId = this.$route.query.ardrId
    this.applyRoomDiscountRecordDetailsInfo.conditions.roomName = this.$route.query.roomName
    this.applyRoomDiscountRecordDetailsInfo.conditions.state = this.$route.query.state
    this.applyRoomDiscountRecordDetailsInfo.conditions.communityId = getCommunityId()
    this._listApplyRoomDiscountRecordDetails()
  },
  methods: {
    async _listApplyRoomDiscountRecordDetails() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.applyRoomDiscountRecordDetailsInfo.conditions
        }
        const { data, total } = await queryApplyRoomDiscountRecordDetail(params)
        this.applyRoomDiscountRecordDetailsInfo.applyRoomDiscountRecordDetails = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('Failed to fetch apply room discount record details:', error)
        this.hasError = true
        this.errorMessage = this.$t('listApplyRoomDiscountRecordDetails.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listApplyRoomDiscountRecordDetails()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listApplyRoomDiscountRecordDetails()
    },
    _goBack() {
      this.$router.go(-1)
    },
    showImg(url) {
      this.$refs.viewImage.open(url)
    },
    handleImageError(e) {
      e.target.src = '/img/noPhoto.jpg'
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.listApplyRoomDiscountRecordDetails-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 媒体预览 */
.ds-media {
  position: relative;
  display: inline-block;
  cursor: pointer;

  &__thumb {
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: var(--ds-radius-sm);
  }

  &__mask {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 50px;
    height: 50px;
    pointer-events: none;
  }

  &__video {
    width: 200px;
    height: 200px;
    object-fit: cover;
    border-radius: var(--ds-radius-sm);
  }
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}
</style>
